import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import MMP from '../mmp/MMP';
import '../../styles/App.css';
import { Col, Container, Row, Form, Spinner } from 'react-bootstrap';
import { useAuthentication, Authentication } from '../auth';
import { LogOut } from '../auth/LogOut';
import { useAuthContext, UserAuthenticated } from '../../context';
import { Signpost } from '../signpost';
import { Navigate } from 'react-router-dom';
import { Navigation } from '../nav';
import { mmpPages } from '../mmp/config';
import { BrowserRouter as Router } from 'react-router-dom';
import { config } from '../../config';
import { Moon, Sun } from 'react-bootstrap-icons';
import Conditions from '../mmp/Conditions';
import { DEFAULT_LOCATION, resolveLocationFromUrl, resolveTitleFromHost } from '../../location';

function LocationAwareFallback() {
  const location = useLocation();
  const pathSegment = location.pathname.split('/').filter(Boolean)[0];
  const featuresByLocation = config.featuresByLocation as Record<string, unknown> | undefined;
  const fallbackPath = pathSegment && featuresByLocation?.[pathSegment] ? `/${pathSegment}` : '/';

  return <Navigate to={fallbackPath} replace />;
}

function App() {
  const { authStatus, userLocation } = useAuthContext();
  const { refreshLogin } = useAuthentication();
  const featuresByLocation = config.featuresByLocation as
    | Record<string, { enableMMP?: boolean; enableDarkMode?: boolean }>
    | undefined;
  const locationByHost = config.locationByHost as Record<string, string> | undefined;
  const titleByHost = config.titleByHost as Record<string, string> | undefined;
  const title = resolveTitleFromHost({
    hostname: window.location.hostname,
    titleByHost,
    defaultTitle: 'Cuikbook',
  });
  const { locationId, isPathScoped, isKnownLocation } = resolveLocationFromUrl({
    pathname: window.location.pathname,
    hostname: window.location.hostname,
    featuresByLocation,
    locationByHost,
    defaultLocation: DEFAULT_LOCATION,
  });
  const locationPrefix = isKnownLocation && isPathScoped ? `/${locationId}` : '';
  const loginPath = `${locationPrefix}/login`;
  const defaultFeatures = featuresByLocation?.penicuik ||
    featuresByLocation?.['/penicuik'] || { enableMMP: false, enableDarkMode: false };
  const locationFeatures =
    featuresByLocation?.[locationId] || featuresByLocation?.[`/${locationId}`] || defaultFeatures;
  const enableMMP = locationFeatures.enableMMP ?? false;
  const enableDarkMode = locationFeatures.enableDarkMode ?? false;
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    if (authStatus === UserAuthenticated.UNKNOWN) {
      refreshLogin();
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <Router>
      <Container>
        <Row>
          <Col sm={1}></Col>
          <Col sm={9}>
            <Row>
              <Col>
                <Navigation />
              </Col>
            </Row>
          </Col>
          <Col sm={2} className="d-flex justify-content-end align-items-center gap-2">
            {authStatus === UserAuthenticated.UNKNOWN ? (
              <Spinner variant="info" size="sm">
                <span className="visually-hidden">Authenticating...</span>
              </Spinner>
            ) : authStatus === UserAuthenticated.YES ? (
              <LogOut />
            ) : (
              <NavLink
                data-testid="login"
                to={loginPath}
                state={{ from: window.location.pathname }}
              >
                Login
              </NavLink>
            )}
            {enableDarkMode && (
              <Form.Check
                type="switch"
                id="global-dark-mode-switch"
                label={darkMode ? <Moon size={20} /> : <Sun size={20} />}
                checked={darkMode}
                onChange={toggleDarkMode}
              />
            )}
          </Col>
        </Row>

        <Routes>
          <Route
            path="/"
            element={
              <Signpost
                isAdmin={authStatus === UserAuthenticated.YES && userLocation === locationId}
              />
            }
          />
          <Route
            path="/:locationId"
            element={
              <Signpost
                isAdmin={authStatus === UserAuthenticated.YES && userLocation === locationId}
              />
            }
          />
          <Route path="/login" element={<Authentication />} />
          <Route path="/:locationId/login" element={<Authentication />} />
          {enableMMP &&
            mmpPages.flatMap((pageConfig) => [
              <Route
                key={pageConfig.route}
                path={pageConfig.route}
                element={<MMP {...pageConfig} />}
              />,
              <Route
                key={`scoped-${pageConfig.route}`}
                path={`/:locationId${pageConfig.route}`}
                element={<MMP {...pageConfig} />}
              />,
            ])}
          <Route path="/conditions" element={<Conditions />} />
          <Route path="/:locationId/conditions" element={<Conditions />} />
          <Route path="*" element={<LocationAwareFallback />} />
        </Routes>
        <Row className="app-version-footer">
          <Col></Col>
          <Col></Col>
          <Col>Version: 1.0.0.56</Col>
        </Row>
      </Container>
    </Router>
  );
}

export default App;
